import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  CircularProgress,
  Typography,
  Box,
  TextField,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
} from "@mui/material";
import { postApiCall } from "../../services/apiHelper";
import { baseUrl } from "../../config/config";

export interface DocumentForPdf {
  id: number;
  assetPlanID: string;
  displayName: string;
  sectionName: string;
  fileType: string;
  fileSize: number;
  fileKey: string;
  preSignedUrl: string;
  uploadedDate: string;
}

interface PrintWithDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedDocuments: DocumentForPdf[]) => void;
  assetPlanId: string;
  loading: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const PrintWithDocumentsDialog: React.FC<PrintWithDocumentsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  assetPlanId,
  loading,
}) => {
  const [documents, setDocuments] = useState<DocumentForPdf[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [nameFilter, setNameFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    if (open && assetPlanId) {
      fetchDocuments();
    }
    // Reset selections and filters when dialog opens
    if (open) {
      setSelectedKeys(new Set());
      setNameFilter("");
      setSectionFilter("");
      setTypeFilter("");
    }
  }, [open, assetPlanId]);

  const fetchDocuments = async () => {
    setFetchLoading(true);
    setError(null);
    try {
      let docs: DocumentForPdf[] = [];
      try {
        const response: any = await postApiCall(
          `${baseUrl}Document/GetDocumentsForPdfExport`,
          { assetPlanId: assetPlanId }
        );
        docs = response?.documents || [];
      } catch {
        // Fallback to existing endpoint
        const response: any = await postApiCall(
          `${baseUrl}Document/GetDocumentsByAssetPlanId`,
          { assetPlanId: assetPlanId }
        );
        const rawDocs = response?.documents || [];
        docs = rawDocs.map((doc: any) => ({
          id: doc.id,
          assetPlanID: doc.assetPlanID,
          displayName: doc.displayName || doc.fileKey?.split("/").pop() || "Unknown",
          sectionName: doc.sectionName || "",
          fileType: doc.fileType || "",
          fileSize: doc.fileSize || 0,
          fileKey: doc.fileKey || "",
          preSignedUrl: "",
          uploadedDate: doc.uploadedDate || "",
        }));
      }
      setDocuments(docs);
    } catch (err) {
      console.error("Error fetching documents for PDF:", err);
      setError("Failed to load documents. Please try again.");
      setDocuments([]);
    } finally {
      setFetchLoading(false);
    }
  };

  // Supported file types that can be rendered in the PDF
  const supportedFileTypes = [".pdf", ".docx", ".doc", ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".csv", ".txt"];

  // Sections to exclude from the popup (already part of DAP PDF content)
  // Values are lowercase with no spaces to match normalized comparison
  const excludedSections = ["propertyimages", "siteplan", "stackingplan"];

  // Derive unique sections and types for dropdown filters (from filtered documents, not all)
  const uniqueSections = useMemo(() => {
    const allowedDocs = documents.filter((doc) => {
      const section = (doc.sectionName || "").toLowerCase().replace(/\s+/g, "");
      return !excludedSections.includes(section);
    });
    const sections = Array.from(new Set(allowedDocs.map((d) => d.sectionName).filter(Boolean)));
    return sections.sort();
  }, [documents]);

  const uniqueTypes = useMemo(() => {
    const types = Array.from(new Set(documents.map((d) => d.fileType?.replace(".", "").toUpperCase()).filter(Boolean)));
    return types.sort();
  }, [documents]);

  // Filtered documents — exclude unsupported types and excluded sections
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      // Derive file type from fileType field or from the file name/key
      let ft = (doc.fileType || "").toLowerCase().trim();
      if (!ft && doc.displayName) {
        const lastDot = doc.displayName.lastIndexOf(".");
        if (lastDot > -1) ft = doc.displayName.substring(lastDot).toLowerCase();
      }
      if (!ft && doc.fileKey) {
        const lastDot = doc.fileKey.lastIndexOf(".");
        if (lastDot > -1) ft = doc.fileKey.substring(lastDot).toLowerCase();
      }
      // Normalize: ensure it starts with a dot
      if (ft && !ft.startsWith(".")) ft = "." + ft;

      const section = (doc.sectionName || "").toLowerCase().replace(/\s+/g, "");
      const isSupported = supportedFileTypes.includes(ft);
      const isSectionAllowed = !excludedSections.includes(section);

      // Debug: log documents being filtered out
      if (!isSupported || !isSectionAllowed) {
        console.log(`[PDF+Docs] Filtered out: "${doc.displayName}", fileType="${doc.fileType}", derived="${ft}", section="${doc.sectionName}", supported=${isSupported}, sectionAllowed=${isSectionAllowed}`);
      }

      if (!isSupported || !isSectionAllowed) return false;
      const matchesName = !nameFilter || doc.displayName.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesSection = !sectionFilter || doc.sectionName === sectionFilter;
      const matchesType = !typeFilter || ft.replace(".", "").toUpperCase() === typeFilter;
      return matchesName && matchesSection && matchesType;
    });
  }, [documents, nameFilter, sectionFilter, typeFilter]);

  const handleToggle = (fileKey: string) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileKey)) {
        newSet.delete(fileKey);
      } else {
        newSet.add(fileKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all filtered documents
      const filteredKeys = filteredDocuments.map((doc) => doc.fileKey);
      setSelectedKeys((prev) => {
        const newSet = new Set(prev);
        filteredKeys.forEach((key) => newSet.add(key));
        return newSet;
      });
    } else {
      // Deselect all filtered documents
      const filteredKeys = new Set(filteredDocuments.map((doc) => doc.fileKey));
      setSelectedKeys((prev) => {
        const newSet = new Set(prev);
        filteredKeys.forEach((key) => newSet.delete(key));
        return newSet;
      });
    }
  };

  const handleConfirm = () => {
    const selectedDocs = documents.filter((doc) =>
      selectedKeys.has(doc.fileKey)
    );
    onConfirm(selectedDocs);
  };

  const filteredSelectedCount = filteredDocuments.filter((doc) => selectedKeys.has(doc.fileKey)).length;
  const isAllFilteredSelected = filteredDocuments.length > 0 && filteredSelectedCount === filteredDocuments.length;
  const isIndeterminate = filteredSelectedCount > 0 && filteredSelectedCount < filteredDocuments.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#253746", color: "#fff", fontWeight: 500 }}>
        Select Documents to Attach to PDF
      </DialogTitle>
      <DialogContent dividers>
        {fetchLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : documents.length === 0 ? (
          <Typography align="center" color="textSecondary">
            No documents found for this asset plan.
          </Typography>
        ) : (
          <>
            {/* Supported file types notification */}
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              <strong>Supported formats:</strong> PDF, DOCX, Images (JPG, PNG, GIF, BMP), CSV, and TXT files are shown below and will be rendered in the exported PDF.
              <br />
              <strong>Note:</strong> XLSX, PPT and other unsupported file types are excluded from this list.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              For optimal performance, the combined size of selected attachments should not exceed 40 MB. Larger selections may result in the export failing or the browser becoming unresponsive.
            </Alert>

            {/* Filter Row */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Search by Name"
                variant="outlined"
                size="small"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{ flex: 2 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={sectionFilter}
                  label="Section"
                  onChange={(e) => setSectionFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {uniqueSections.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Results count */}
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Showing {filteredDocuments.length} of {documents.length} documents
              {selectedKeys.size > 0 && ` • ${selectedKeys.size} selected`}
            </Typography>

            {/* Documents Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader aria-label="documents table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ backgroundColor: "#253746" }}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllFilteredSelected}
                        onChange={handleSelectAll}
                        inputProps={{ "aria-label": "select all documents" }}
                        sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#253746", color: "#fff", fontWeight: 600 }}>Document Name</TableCell>
                    <TableCell sx={{ backgroundColor: "#253746", color: "#fff", fontWeight: 600 }}>Section</TableCell>
                    <TableCell sx={{ backgroundColor: "#253746", color: "#fff", fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ backgroundColor: "#253746", color: "#fff", fontWeight: 600 }}>Size</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary" sx={{ py: 2 }}>
                          No documents match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDocuments.map((doc) => (
                      <TableRow
                        key={doc.fileKey}
                        hover
                        onClick={() => handleToggle(doc.fileKey)}
                        role="checkbox"
                        aria-checked={selectedKeys.has(doc.fileKey)}
                        selected={selectedKeys.has(doc.fileKey)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedKeys.has(doc.fileKey)}
                            inputProps={{ "aria-labelledby": `doc-${doc.id}` }}
                          />
                        </TableCell>
                        <TableCell id={`doc-${doc.id}`}>{doc.displayName}</TableCell>
                        <TableCell>{doc.sectionName}</TableCell>
                        <TableCell>{doc.fileType?.replace(".", "").toUpperCase()}</TableCell>
                        <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          className="primary-outline"
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            borderColor: "#2b8818",
            color: "#2b8818",
            "&:hover": {
              backgroundColor: "#2b8818",
              color: "#fff",
              borderColor: "#2b8818",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          className="primary"
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: "#2b8818",
            "&:hover": { backgroundColor: "#236e13" },
            "&.Mui-disabled": { backgroundColor: "#ccc" },
          }}
        >
          {loading ? "Generating PDF..." : selectedKeys.size > 0 ? `Export to PDF (${selectedKeys.size} attached)` : "Export to PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintWithDocumentsDialog;
